import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import OptionSelect from '@/pages/apps/components/option-select';
import { app_path, billing_path } from '@/rails/routes';
import type { AppShell } from '@/types/platform';
import { Form, Link, usePage } from '@inertiajs/react';
import { useState, type ReactNode } from 'react';

const PAYMENT_MODES = [
  { value: 'test', label: 'Test' },
  { value: 'live', label: 'Live' },
];

// One muted line for a setting the plan does not include. The plan names match
// the ones the server gives when it refuses the setting.
const Locked = ({ children }: { children: ReactNode }) => {
  return <p className="text-muted-foreground text-sm">{children}</p>;
};

const Hint = ({ id, children }: { id: string; children: ReactNode }) => {
  return (
    <p id={id} className="text-muted-foreground text-xs">
      {children}
    </p>
  );
};

// The settings an owner can change. A setting the plan does not include is not
// an input here, so it is never sent.
const SettingsCard = ({ app }: { app: AppShell }) => {
  const [paymentMode, setPaymentMode] = useState<AppShell['paymentMode']>(app.paymentMode);
  const { staff } = usePage<{ staff: boolean }>().props;
  const { features } = app;
  const hasFromName = features.sender !== 'shared';
  const hasSendingDomain = features.sender === 'domain';
  const anythingLocked =
    !hasFromName ||
    !hasSendingDomain ||
    !features.customDomain ||
    !features.regionChoice ||
    !features.livePayments;

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle>
          <h2>Settings</h2>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form
          method="patch"
          action={app_path(app.id)}
          options={{ preserveScroll: true }}
          className="space-y-5"
        >
          {({ processing }) => (
            <>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="settings-name">App name</Label>
                  <Input id="settings-name" name="app[name]" defaultValue={app.name} required />
                </div>

                {hasFromName ? (
                  <div className="space-y-2">
                    <Label htmlFor="settings-from-name">Sender name</Label>
                    <Input
                      id="settings-from-name"
                      name="app[from_name]"
                      defaultValue={app.fromName ?? ''}
                      aria-describedby="settings-from-name-hint"
                    />
                    <Hint id="settings-from-name-hint">
                      The name on the emails this app sends. Leave it blank to use your company
                      name.
                    </Hint>
                  </div>
                ) : null}

                {hasSendingDomain && !staff ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Sending domain</p>
                    <p className="text-sm">{app.sendingDomain || 'Not set up yet'}</p>
                    <p className="text-muted-foreground text-xs">
                      CAP Rock Labs sets this up with you, because the domain has to be verified
                      first. Email us to start or change it.
                    </p>
                  </div>
                ) : null}

                {hasSendingDomain && staff ? (
                  <div className="space-y-2">
                    <Label htmlFor="settings-sending-domain">Sending domain</Label>
                    <Input
                      id="settings-sending-domain"
                      name="app[sending_domain]"
                      defaultValue={app.sendingDomain ?? ''}
                      placeholder="example.com"
                      autoCapitalize="none"
                      spellCheck={false}
                      aria-describedby="settings-sending-domain-hint"
                    />
                    <Hint id="settings-sending-domain-hint">
                      Emails go out from noreply@ this domain. Verify the domain with the mail
                      provider before you set it. Only staff see this field.
                    </Hint>
                  </div>
                ) : null}

                {features.customDomain ? (
                  <div className="space-y-2">
                    <Label htmlFor="settings-custom-domain">Custom domain</Label>
                    <Input
                      id="settings-custom-domain"
                      name="app[custom_domain]"
                      defaultValue={app.customDomain ?? ''}
                      placeholder="book.example.com"
                      autoCapitalize="none"
                      spellCheck={false}
                      aria-describedby="settings-custom-domain-hint"
                    />
                    <Hint id="settings-custom-domain-hint">
                      The web address you want for this app. CAP Rock Labs points it at your app
                      with you. Until then the app stays at its CAP Rock Labs address.
                    </Hint>
                  </div>
                ) : null}

                {features.regionChoice ? (
                  <div className="space-y-2">
                    <Label htmlFor="settings-region">Region</Label>
                    <Input
                      id="settings-region"
                      name="app[region]"
                      defaultValue={app.region ?? ''}
                      autoCapitalize="none"
                      spellCheck={false}
                      aria-describedby="settings-region-hint"
                    />
                    <Hint id="settings-region-hint">
                      Where this app keeps its records. It is used when the app is first set up.
                    </Hint>
                  </div>
                ) : null}

                {features.livePayments ? (
                  <div className="space-y-2">
                    <Label htmlFor="settings-payment-mode">Payments</Label>
                    <OptionSelect
                      id="settings-payment-mode"
                      name="app[payment_mode]"
                      value={paymentMode}
                      onChange={(value) => setPaymentMode(value === 'live' ? 'live' : 'test')}
                      options={PAYMENT_MODES}
                      describedBy="settings-payment-mode-hint"
                    />
                    <Hint id="settings-payment-mode-hint">
                      Test is for trying things out. Live takes real payments.
                    </Hint>
                  </div>
                ) : null}
              </div>

              {anythingLocked ? (
                <div className="space-y-1 border-t pt-4">
                  {hasFromName ? null : <Locked>A sender name is part of Build or Studio.</Locked>}
                  {hasSendingDomain ? null : (
                    <Locked>Sending from your own domain is part of Studio.</Locked>
                  )}
                  {features.customDomain ? null : (
                    <Locked>A custom domain is part of Build or Studio.</Locked>
                  )}
                  {features.regionChoice ? null : (
                    <Locked>Choosing a region is part of Studio.</Locked>
                  )}
                  {features.livePayments ? null : (
                    <Locked>Live payments are part of Build or Studio.</Locked>
                  )}
                  <p className="text-sm">
                    <Link href={billing_path()} className="font-medium underline">
                      See plans
                    </Link>
                  </p>
                </div>
              ) : null}

              <Button type="submit" disabled={processing}>
                Save settings
              </Button>
            </>
          )}
        </Form>
      </CardContent>
    </Card>
  );
};

export default SettingsCard;
